import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL } from '../config/apiConfig';
import ChartPublicoOBS from './ChartPublicoOBS';
import { ReactComponent as CallIcon } from '../assets/icons/call.svg';
import { ReactComponent as PublicIcon } from '../assets/icons/public.svg';
import { ReactComponent as FiftyIcon } from '../assets/icons/fifty.svg';
import WizardMagoIcon from '../assets/icons/wizard-mago.jsx';
import buildImageUrl from '../config/imageUrlHelper';

// Intervalo de sondeo del estado (ms). El navegador del concurso empuja el
// estado (throttle ~250ms); aquí lo leemos por token para reflejarlo en OBS.
const POLL_MS = 400;

// Base de la API para el overlay. En producción usamos el MISMO origen desde el
// que OBS ha cargado la página (evita problemas de CORS/redirecciones entre
// www y el dominio raíz). En local (XAMPP) usamos la ruta de desarrollo.
const getOverlayApiBase = () => {
  const host = window.location.hostname;
  const esLocal = host === 'localhost' || host === '127.0.0.1';
  return esLocal ? API_BASE_URL : `${window.location.origin}/api`;
};

const IDENTIFICADORES = ['a)', 'b)', 'c)', 'd)'];

// -- Texto de la pregunta en una sola línea, reduciendo la fuente hasta que quepa --
const AutoFitPregunta = ({ texto, max = 58, min = 16 }) => {
  const contenedorRef = useRef(null);
  const spanRef = useRef(null);
  const [fontSize, setFontSize] = useState(max);

  useLayoutEffect(() => {
    const ajustar = () => {
      const contenedor = contenedorRef.current;
      const span = spanRef.current;
      if (!contenedor || !span) return;

      let size = max;
      span.style.fontSize = `${size}px`;
      let guarda = 0;
      while (span.scrollWidth > contenedor.clientWidth && size > min && guarda < 400) {
        size -= 1;
        span.style.fontSize = `${size}px`;
        guarda += 1;
      }
      setFontSize(size);
    };

    ajustar();
    window.addEventListener('resize', ajustar);
    return () => window.removeEventListener('resize', ajustar);
  }, [texto, max, min]);

  return (
    <div ref={contenedorRef} style={{ width: '100%', overflow: 'hidden', textAlign: 'center' }}>
      <span
        ref={spanRef}
        style={{
          fontSize: `${fontSize}px`,
          whiteSpace: 'nowrap',
          display: 'inline-block',
          fontWeight: 800,
          color: '#fff',
          textShadow: '0 2px 8px rgba(0,0,0,0.85), 0 0 2px rgba(0,0,0,0.9)',
          lineHeight: 1.15,
        }}
      >
        {texto}
      </span>
    </div>
  );
};

// -- Barra de progreso con círculos (verde acierto / rojo fallo / pendiente) --
const BarraProgreso = ({ progreso, preguntaActual, total }) => {
  if (!total || total < 1) return null;
  const porcentaje = total > 1 ? (preguntaActual / (total - 1)) * 100 : 0;

  return (
    <div style={{ position: 'relative', height: 22, width: '100%' }}>
      {/* Pista */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: 0,
          width: '100%',
          height: 8,
          transform: 'translateY(-50%)',
          background: 'rgba(148,163,184,0.35)',
          borderRadius: 9999,
        }}
      >
        <div
          style={{
            height: 8,
            width: `${porcentaje}%`,
            background: '#2563eb',
            borderRadius: 9999,
            transition: 'width 300ms ease',
          }}
        />
      </div>
      {/* Círculos */}
      <div style={{ position: 'absolute', top: '50%', left: 0, width: '100%', transform: 'translateY(-50%)' }}>
        {progreso.map((estado, index) => {
          const posicion = total > 1 ? (index / (total - 1)) * 100 : 50;
          let fondo = '#fff';
          let borde = '2px solid #cbd5e1';
          if (estado === 'acierto') {
            fondo = '#22c55e';
            borde = '2px solid #16a34a';
          } else if (estado === 'fallo') {
            fondo = '#ef4444';
            borde = '2px solid #dc2626';
          }
          return (
            <div
              key={index}
              style={{
                position: 'absolute',
                left: `${posicion}%`,
                transform: 'translate(-50%, -50%)',
                width: 16,
                height: 16,
                borderRadius: '9999px',
                background: fondo,
                border: borde,
                boxShadow: '0 1px 4px rgba(0,0,0,0.5)',
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

// -- Icono de comodín (con estado usado/tachado) --
const ComodinIcono = ({ children, color, usado }) => (
  <div style={{ position: 'relative', display: 'inline-flex' }}>
    <div
      style={{
        width: 54,
        height: 54,
        borderRadius: '9999px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        background: usado ? '#6b7280' : color,
        opacity: usado ? 0.55 : 1,
        boxShadow: '0 2px 10px rgba(0,0,0,0.55)',
      }}
    >
      {children}
    </div>
    {usado && (
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 62,
          height: 4,
          background: '#ef4444',
          borderRadius: 9999,
          transform: 'translate(-50%, -50%) rotate(-45deg)',
          boxShadow: '0 0 3px rgba(0,0,0,0.6)',
        }}
      />
    )}
  </div>
);

const OBSView = () => {
  const [estado, setEstado] = useState(null);
  const [error, setError] = useState(null);

  const token = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || '';
  }, []);

  // Fondo totalmente transparente para el Browser Source de OBS
  useEffect(() => {
    const previos = {
      htmlBg: document.documentElement.style.background,
      bodyBg: document.body.style.background,
    };
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
    const root = document.getElementById('root');
    if (root) root.style.background = 'transparent';
    // Ocultar el fondo global animado (aurora) para no romper la transparencia de OBS
    document.documentElement.classList.add('obs-transparent');
    return () => {
      document.documentElement.style.background = previos.htmlBg;
      document.body.style.background = previos.bodyBg;
      document.documentElement.classList.remove('obs-transparent');
    };
  }, []);

  const sondear = useCallback(async () => {
    if (!token) return;
    try {
      const url = `${getOverlayApiBase()}/overlay_estado.php?token=${encodeURIComponent(token)}`;
      const respuesta = await fetch(url, { cache: 'no-store' });
      if (!respuesta.ok) {
        if (respuesta.status === 404) setError('Token no válido');
        return;
      }
      const data = await respuesta.json();
      setError(null);
      setEstado(data && data.success ? data.estado : null);
    } catch (_) {
      // Silencioso: reintentará en el siguiente ciclo
    }
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    let activo = true;
    let enCurso = false;

    const ciclo = async () => {
      if (!activo || enCurso) return;
      enCurso = true;
      await sondear();
      enCurso = false;
    };

    ciclo();
    const id = setInterval(ciclo, POLL_MS);
    return () => {
      activo = false;
      clearInterval(id);
    };
  }, [token, sondear]);

  // Sin token: pista mínima (no aparece en OBS si la URL está bien formada)
  if (!token) {
    return (
      <div style={{ padding: 24, color: '#fff', fontFamily: 'sans-serif' }}>
        Falta el parámetro <code>?token=</code> en la URL del overlay.
      </div>
    );
  }

  const finalizado = Boolean(estado && estado.juegoTerminado);
  const hayConcurso = Boolean(estado && estado.activo && estado.pregunta);

  // Sin concurso activo ni resumen final: overlay vacío y transparente
  if (!hayConcurso && !finalizado) {
    return <div style={{ width: '100vw', height: '100vh', background: 'transparent' }} />;
  }

  const {
    pregunta,
    autorFoto = '',
    autorNombre = '',
    respuestas = [],
    respuestasVisibles = 0,
    respuestasEliminadas = [],
    respuestaSeleccionada,
    respuestaRevelada,
    indiceRespuestaCorrecta,
    progreso = [],
    preguntaActual = 0,
    totalPreguntas = 0,
    puntuacion = 0,
    explicacion = '',
    explicacionImagen = '',
    comodines = {},
    llamada = {},
    publico = {},
    soborno = {},
  } = estado;

  // Animación común de entrada/salida para la zona de modales
  const anim = {
    initial: { opacity: 0, scale: 0.92, y: 14 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.92, y: 14 },
    transition: { duration: 0.28, ease: 'easeOut' },
  };

  const porcentajeAciertos = totalPreguntas > 0 ? Math.round((puntuacion / totalPreguntas) * 100) : 0;

  const estiloRespuesta = (index) => {
    const eliminada = respuestasEliminadas.includes(index);
    const visible = index < respuestasVisibles && !eliminada;

    const base = {
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      padding: '10px 16px',
      borderRadius: 12,
      fontSize: 20,
      fontWeight: 600,
      border: '2px solid rgba(148,163,184,0.35)',
      background: 'rgba(15,23,42,0.82)',
      color: '#fff',
      boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
      textShadow: '0 1px 3px rgba(0,0,0,0.8)',
      overflow: 'hidden',
      boxSizing: 'border-box',
    };

    if (eliminada) {
      return { ...base, opacity: 0.35 };
    }
    if (!visible) {
      return { ...base, background: 'rgba(30,41,59,0.6)', color: 'rgba(226,232,240,0.6)', fontStyle: 'italic' };
    }
    if (respuestaRevelada) {
      if (index === indiceRespuestaCorrecta) {
        return { ...base, background: 'rgba(22,163,74,0.94)', border: '2px solid #16a34a' };
      }
      if (index === respuestaSeleccionada) {
        return { ...base, background: 'rgba(220,38,38,0.94)', border: '2px solid #dc2626' };
      }
      return base;
    }
    if (respuestaSeleccionada === index) {
      return { ...base, background: 'rgba(202,138,4,0.94)', border: '2px solid #eab308' };
    }
    return base;
  };

  const textoRespuesta = (index) => {
    const eliminada = respuestasEliminadas.includes(index);
    const visible = index < respuestasVisibles && !eliminada;
    if (eliminada) return 'Respuesta eliminada';
    if (!visible) return 'Respuesta oculta';
    return respuestas[index]?.texto ?? '';
  };

  const tiempoLlamada = Number(llamada.tiempo) || 0;
  const anchoLlamada = Math.max(0, Math.min(100, (tiempoLlamada / 30) * 100));

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        background: 'transparent',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {/*
        Cada bloque tiene ALTURA RESERVADA FIJA y separación propia. Así, aunque la
        pregunta cambie de tamaño de fuente o llegue a 2 líneas, NO desplaza a los
        demás elementos. Esto permite recortar cada zona del overlay en OBS y
        colocarla por separado sin que se muevan entre concursos/preguntas.
      */}
      {hayConcurso && (
      <div
        style={{
          position: 'absolute',
          top: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(1200px, 92vw)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        {/* Barra de progreso (banda fija) */}
        <div style={{ height: 28, marginBottom: 34, display: 'flex', alignItems: 'center' }}>
          <div style={{ width: '100%' }}>
            <BarraProgreso progreso={progreso} preguntaActual={preguntaActual} total={totalPreguntas} />
          </div>
        </div>

        {/* Pregunta (banda fija, centrada). Si el autor es un usuario real con
            foto, se muestra a la derecha; va en posición absoluta para no
            descolocar la pregunta. El padding reserva su hueco cuando hay foto. */}
        <div
          style={{
            position: 'relative',
            height: 132,
            marginBottom: 34,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: autorFoto ? '0 96px' : 0,
          }}
        >
          {pregunta && <AutoFitPregunta texto={pregunta} />}
          {autorFoto && (
            <img
              src={buildImageUrl(autorFoto)}
              alt={autorNombre ? `Foto de ${autorNombre}` : 'Foto del autor'}
              title={autorNombre || undefined}
              style={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 76,
                height: 76,
                borderRadius: '9999px',
                objectFit: 'cover',
                border: '3px solid rgba(255,255,255,0.9)',
                boxShadow: '0 3px 14px rgba(0,0,0,0.65)',
              }}
              onError={(event) => {
                event.currentTarget.style.display = 'none';
              }}
            />
          )}
        </div>

        {/* Respuestas (filas de altura fija) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridAutoRows: '78px', gap: 16, marginBottom: 34 }}>
          {[0, 1, 2, 3].map((index) => (
            <div key={index} style={estiloRespuesta(index)}>
              <span style={{ fontWeight: 800, marginRight: 8, flexShrink: 0 }}>{IDENTIFICADORES[index]}</span>
              <span
                style={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  overflow: 'hidden',
                }}
              >
                {textoRespuesta(index)}
              </span>
            </div>
          ))}
        </div>

        {/* Comodines (banda fija) */}
        <div style={{ height: 60, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 20 }}>
          <ComodinIcono color="#7c3aed" usado={!(comodines.llamada > 0)}>
            <CallIcon style={{ width: 28, height: 28 }} />
          </ComodinIcono>
          <ComodinIcono color="#ea580c" usado={!(comodines.publico > 0)}>
            <PublicIcon style={{ width: 28, height: 28 }} />
          </ComodinIcono>
          <ComodinIcono color="#dc2626" usado={!(comodines.cincuenta > 0)}>
            <FiftyIcon style={{ width: 28, height: 28 }} />
          </ComodinIcono>
          <ComodinIcono color="#2563eb" usado={!(comodines.mago > 0) || puntuacion < 4}>
            <WizardMagoIcon style={{ width: 28, height: 28, fill: '#fff' }} />
          </ComodinIcono>
        </div>
      </div>
      )}

      {/*
        ZONA ÚNICA para las acciones de comodines y ventanas modales. Posición fija y
        separada del resto de elementos: se puede recortar SOLO esta zona en OBS.
        Aparecen/desaparecen solas y, si no hay nada activo, queda totalmente transparente.
      */}
      <div
        style={{
          position: 'absolute',
          top: 540,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(700px, 94vw)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          pointerEvents: 'none',
        }}
      >

      <AnimatePresence>

      {/* Estado: temporizador de la llamada */}
      {hayConcurso && llamada.visible && (
        <motion.div
          key="llamada"
          initial={anim.initial}
          animate={anim.animate}
          exit={anim.exit}
          transition={anim.transition}
          style={{
            background: 'rgba(88,28,135,0.92)',
            color: '#fff',
            borderRadius: 16,
            padding: '16px 28px',
            minWidth: 320,
            textAlign: 'center',
            boxShadow: '0 8px 30px rgba(0,0,0,0.6)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
            <CallIcon style={{ width: 26, height: 26 }} />
            <span style={{ fontWeight: 700, fontSize: 18 }}>Llamada a un amigo</span>
          </div>
          <div style={{ fontSize: 52, fontWeight: 900, lineHeight: 1 }}>{tiempoLlamada}s</div>
          <div style={{ marginTop: 10, height: 8, background: 'rgba(255,255,255,0.25)', borderRadius: 9999 }}>
            <div
              style={{
                height: 8,
                width: `${anchoLlamada}%`,
                background: tiempoLlamada <= 10 ? '#ef4444' : '#a855f7',
                borderRadius: 9999,
                transition: 'width 500ms linear',
              }}
            />
          </div>
        </motion.div>
      )}

      {/* Comodín del público: solo el gráfico circular y los porcentajes */}
      {hayConcurso && publico.visible && (
        <motion.div
          key="publico"
          initial={anim.initial}
          animate={anim.animate}
          exit={anim.exit}
          transition={anim.transition}
          style={{ background: 'transparent' }}
        >
          <ChartPublicoOBS datos={publico.datos} tiempo={publico.tiempo} />
        </motion.div>
      )}

      {/* Ventana: "¿Has sobornado al presentador?" */}
      {hayConcurso && soborno.visible && (
        <motion.div
          key="soborno"
          initial={anim.initial}
          animate={anim.animate}
          exit={anim.exit}
          transition={anim.transition}
          style={{
            background: 'rgba(15,23,42,0.96)',
            color: '#fff',
            borderRadius: 18,
            padding: '22px 26px',
            width: 'min(420px, 92vw)',
            textAlign: 'center',
            boxShadow: '0 10px 40px rgba(0,0,0,0.65)',
            border: '1px solid rgba(148,163,184,0.35)',
          }}
        >
          <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 18 }}>¿Has sobornado al presentador?</h2>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center' }}>
            <span style={{ flex: 1, background: '#16a34a', borderRadius: 10, padding: '12px 0', fontWeight: 800, fontSize: 18 }}>Sí</span>
            <span style={{ flex: 1, background: '#dc2626', borderRadius: 10, padding: '12px 0', fontWeight: 800, fontSize: 18 }}>No</span>
          </div>
        </motion.div>
      )}

      {/* Explicación de la pregunta (al revelar la respuesta) */}
      {hayConcurso && respuestaRevelada && explicacion && (
        <motion.div
          key="explicacion"
          initial={anim.initial}
          animate={anim.animate}
          exit={anim.exit}
          transition={anim.transition}
          style={{
            background: 'rgba(17,24,39,0.72)',
            color: '#fff',
            borderRadius: 16,
            padding: '16px 20px',
            width: 'min(680px, 94vw)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.55)',
            border: '1px solid rgba(148,163,184,0.25)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6, opacity: 0.9 }}>Explicación</div>
          {explicacionImagen && (
            <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'center' }}>
              <img
                src={buildImageUrl(explicacionImagen)}
                alt="Imagen explicativa"
                style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 10, boxShadow: '0 3px 14px rgba(0,0,0,0.5)' }}
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}
          <div style={{ fontSize: 18, lineHeight: 1.4, textShadow: '0 1px 2px rgba(0,0,0,0.7)' }}>{explicacion}</div>
        </motion.div>
      )}

      {/* Ventana: "Resumen del Concurso" (al finalizar) */}
      {finalizado && (
        <motion.div
          key="resumen"
          initial={anim.initial}
          animate={anim.animate}
          exit={anim.exit}
          transition={anim.transition}
          style={{
            background: 'rgba(15,23,42,0.96)',
            color: '#fff',
            borderRadius: 20,
            padding: '28px 32px',
            width: 'min(520px, 92vw)',
            textAlign: 'center',
            boxShadow: '0 12px 48px rgba(0,0,0,0.7)',
            border: '1px solid rgba(148,163,184,0.35)',
          }}
        >
          <h2 style={{ fontSize: 30, fontWeight: 900, marginBottom: 20 }}>¡Concurso Finalizado!</h2>
          <p style={{ fontSize: 22, marginBottom: 10 }}>
            Puntuación final: <strong>{puntuacion}</strong> de {totalPreguntas}
          </p>
          <p style={{ fontSize: 20, opacity: 0.9 }}>
            Porcentaje de aciertos: <strong>{porcentajeAciertos}%</strong>
          </p>
        </motion.div>
      )}
      </AnimatePresence>
      </div>

      {error && (
        <div style={{ position: 'absolute', bottom: 12, left: 12, color: '#fca5a5', fontSize: 14 }}>{error}</div>
      )}
    </div>
  );
};

export default OBSView;
